/* eslint-disable no-param-reassign */
import _ from 'lodash'
import React, { ForwardRefExoticComponent, RefAttributes, useEffect, useRef, useState } from 'react'
import SignatureCanvas, { SignatureCanvasProps } from 'react-signature-canvas'
import { v4 } from 'uuid'

import colors from 'constants/colors'

import { Icon, Typography } from 'components'
import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'

import i18n from 'translations'

import SignaturePadContainer from './SignaturePadContainer'
import {
  StyledClearContainer,
  StyledPadContainer,
  StyledSignaturePadContainer,
  StyledTextContainer,
  StyledTextWrapper,
  StyledUnderline,
  StyledUnderlineWrapper,
} from './SignaturePadStyled'

const SignaturePadRoot = {
  Container: SignaturePadContainer,
}

export interface SignaturePadProps {
  canvasProps?: any
  disabled?: boolean
  error?: string
  onChange?: (e: SelectedImage) => void
  onWatermarkToggle?: (hide: boolean) => void
  value?: string
}

const SignaturePad = React.forwardRef<RefAttributes<HTMLCanvasElement>, SignaturePadProps>(({
  onChange,
  onWatermarkToggle,
  value,
  ...rest
}: SignaturePadProps, ref: any) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const componentRef = useRef<SignatureCanvasProps>(null)

  const [height, setHeight] = useState<number>(100)
  const [width, setWidth] = useState<number>(100)
  const [hideWaterMark, setHideWaterMark] = useState<boolean>(false)
  const [initialised, setInitialised] = useState<boolean>(false)

  useEffect(() => {
    if (initialised && value && componentRef.current) {
      const canvas = componentRef.current.getCanvas()
      const ctx = canvas.getContext('2d')

      const loadImage = async () => {
        try {
          const response = await fetch(value, { mode: 'cors' })
          const blob = await response.blob()
          const objectUrl = URL.createObjectURL(blob)

          const image = new Image()
          image.src = objectUrl

          image.onload = () => {
            const drawWidth = canvas.width * 0.5
            const drawHeight = canvas.height * 0.5
            const offsetX = (canvas.width - drawWidth) / 2
            const offsetY = (canvas.height - drawHeight) / 2

            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight)

            URL.revokeObjectURL(objectUrl)
            setHideWaterMark(true)
          }
        } catch {}
      }

      loadImage()
    }
  }, [initialised])

  useEffect(() => {
    setHeight(parentRef.current.clientHeight)
    setWidth(parentRef.current.clientWidth)
    setInitialised(true)
  }, [])

  const handleClearSignature = () => {
    componentRef.current.clear()
    setHideWaterMark(false)
    onWatermarkToggle?.(false)

    onChange?.(null)
  }

  const handleEndSignature = () => {
    if (!onChange) {
      return
    }

    const canvas = componentRef.current.getTrimmedCanvas()

    canvas.toBlob((blob) => {
      const file = blob

      file.name = `${v4()}.png`

      onChange({
        file,
        isUploaded: false,
        value: URL.createObjectURL(file),
      })
    }, 'image/png')
  }

  const handleAssignRef = (el: SignatureCanvasProps) => {
    if (el) {
      el.clearSignature = handleClearSignature
    }

    if (ref) {
      ref.current = el
    }

    componentRef.current = el
  }

  const newCanvasProps = {
    height,
    width,
    ...rest.canvasProps,
  }

  return (
    <React.Fragment>
      {!hideWaterMark && (
        <StyledTextContainer>
          <StyledTextWrapper>
            <Icon color={colors.lightGray} height={30} icon="edit" />
            <Typography color={colors.lightGray} fontSize={32} padding="0 0 0 20px" bold>
              {i18n.t('module:Modals:InjurySignature:Signature:signHere')}
            </Typography>
          </StyledTextWrapper>
        </StyledTextContainer>
      )}
      <StyledUnderlineWrapper>
        <StyledUnderline />
      </StyledUnderlineWrapper>
      <StyledSignaturePadContainer>
        <StyledPadContainer ref={parentRef}>
          <SignatureCanvas
            {...rest}
            canvasProps={newCanvasProps}
            ref={handleAssignRef}
            onBegin={() => {
              setHideWaterMark(true)
              onWatermarkToggle?.(true)
            }}
            onEnd={handleEndSignature}
          />
        </StyledPadContainer>
      </StyledSignaturePadContainer>
      <StyledClearContainer>
        <Typography variant="div" primary onClick={handleClearSignature}>
          {_.upperFirst(i18n.t('global:clear'))}
        </Typography>
      </StyledClearContainer>
    </React.Fragment>
  )
})

const withStaticProps = <T extends unknown>(
  forwarded: ForwardRefExoticComponent<SignaturePadProps & RefAttributes<any>>,
  staticProps: T,
) => Object.assign(forwarded, staticProps)

export default withStaticProps(SignaturePad, SignaturePadRoot)
